import { useHeaderHeight } from 'hooks/useHeaderHeight/useHeaderHeight'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { computeInitialTopDistance } from 'notifications/utils/getInitialStyles'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { AnimationEvent, CSSProperties, MouseEvent } from 'react'

import {
	DEFAULT_SNACKBAR_DISTANCE,
	SnackbarPosition,
} from '../../constants/constants'
import { useIsMoving } from '../useIsMoving/useIsMoving'
import { useNotificationsContext } from '../useNotificationsContext'

import styles from 'notifications/components/Snackbar/Snackbar.module.scss'

interface SnackbarStyleProps extends CSSProperties {
	'--wrapper-margin': string
	'--wrapper-top': string
	'--initial-margin': string
}

interface UseSnackbarProps {
	delay: number
	position: SnackbarPosition
	onClick?: (e: MouseEvent<HTMLButtonElement>) => void
	onClose?: () => void
}

export const useSnackbar = ({
	delay,
	position,
	onClick,
	onClose,
}: UseSnackbarProps) => {
	const { headerHeight, headerRef } = useHeaderHeight()
	const { snackbarShow, setSnackbarShow, removeNotification } =
		useNotificationsContext()

	const snackbarRef = useRef<HTMLDivElement | null>(null)
	const expiredRef = useRef(false)
	const isHoverRef = useRef(false)
	const timeoutRef = useRef<number | NodeJS.Timeout | null>(null)

	const observerOptions = {
		threshold: 1.0,
		rootMargin: `${headerHeight}px 0px 0px 0px`,
	}
	const headerEntry = useIntersectionObserver(headerRef, observerOptions)
	const mustRemoveNotification = !snackbarShow && expiredRef.current

	const INITIAL_MARGIN = DEFAULT_SNACKBAR_DISTANCE[position]
	const INITIAL_TOP_DISTANCE = computeInitialTopDistance(
		headerRef,
		headerHeight
	)

	const { isMoving } = useIsMoving({
		snackbarRef,
		position,
		initialMargin: INITIAL_MARGIN,
	})

	const snackbarStyle: SnackbarStyleProps = useMemo(() => {
		return {
			'--wrapper-margin': INITIAL_MARGIN,
			'--wrapper-top': INITIAL_TOP_DISTANCE,
			'--initial-margin': INITIAL_MARGIN,
		}
	}, [INITIAL_MARGIN, INITIAL_TOP_DISTANCE])

	const handleIn = useCallback((): void => {
		isHoverRef.current = true
	}, [])

	const handleOut = useCallback((): void => {
		isHoverRef.current = false

		if (expiredRef.current) {
			addAnimationOut()
		}
	}, [])

	const onOut = useCallback((): void => {
		setSnackbarShow(false)

		if (onClose) {
			onClose()
		}
	}, [onClose])

	const handleOnAnimationEnd = useCallback(
		(e: AnimationEvent<HTMLElement>): void => {
			if (e.animationName.includes('out')) {
				onOut()
			}
		},
		[onOut]
	)

	const handleClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>): void | null => {
			const preventClick = !onClick || isMoving()

			if (preventClick) {
				e.preventDefault()
				e.stopPropagation()
				return null
			}

			if (onClick) {
				onClick(e)
				expiredRef.current = true
				handleOut()
				setSnackbarShow(false)
			}
		},
		[onClick, isMoving, handleOut, setSnackbarShow]
	)

	const addAnimationOut = useCallback((): void => {
		const isHovering = isHoverRef.current
		const alreadyOut = snackbarRef.current?.classList.contains(styles.out)
		const expired = expiredRef.current
		const isSnackbarshouldOut = expired && !isHovering && !alreadyOut

		if (isSnackbarshouldOut) {
			snackbarRef.current?.classList.add(styles.out)
		}
	}, [])

	useEffect(() => {
		if (snackbarShow) {
			timeoutRef.current = setTimeout(() => {
				expiredRef.current = true
				addAnimationOut()
			}, delay)
		}

		if (mustRemoveNotification) {
			removeNotification()
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			if (expiredRef.current) {
				expiredRef.current = false
			}
		}
	}, [snackbarShow])

	useEffect(() => {
		if (headerEntry) {
			snackbarRef.current?.style.setProperty(
				'--wrapper-top',
				headerEntry.isIntersecting
					? `calc(${headerHeight}px + 1.5rem)`
					: '1.5rem'
			)
		}
	}, [headerEntry, headerHeight])

	return {
		handleIn,
		handleOut,
		handleClick,
		handleOnAnimationEnd,
		snackbarRef,
		snackbarStyle,
	}
}
